import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import {
  queryLimits,
  type RelationshipQueryDefinition,
  type RelationshipQueryResult,
} from '@cdr-clarity/contracts';

export type ExecuteRelationshipQuery = (
  query: RelationshipQueryDefinition,
) => Promise<RelationshipQueryResult>;
export interface UnifiedRecordPage {
  readonly result: RelationshipQueryResult;
  readonly nextCursors: readonly string[] | null;
}

/** Join column batches only by the service's exact association membership identity. */
export async function unifiedRecordPage(
  execute: ExecuteRelationshipQuery,
  definition: RelationshipQueryDefinition,
  columns: readonly string[],
  cursors: readonly string[] | null,
): Promise<UnifiedRecordPage> {
  if (definition.levels.length || definition.measures.length)
    throw new CopyError(
      viewerStrings.webStateUnifiedRecordPage.unifiedRecordPagingAcceptsAssociationRowsWithout,
    );
  const batches: string[][] = [];
  for (let start = 0; start < columns.length; start += queryLimits.fields)
    batches.push(columns.slice(start, start + queryLimits.fields));
  if (!batches.length) batches.push([]);
  if (cursors !== null && cursors.length !== batches.length)
    throw new CopyError(
      viewerStrings.webStateUnifiedRecordPage.columnSelectionChangedRestartFromTheFirst,
    );
  const results: RelationshipQueryResult[] = [];
  const identity = (result: RelationshipQueryResult) =>
    JSON.stringify({
      scope: result.query.scope,
      counts: result.counts,
      overlap: result.overlap,
      rows: result.rows.map(({ membershipId, primary, related, relationships, relatedCalls }) => ({
        membershipId,
        primary,
        related,
        relationships,
        relatedCalls,
      })),
    });
  for (const [index, batch] of batches.entries()) {
    const result = await execute({
      ...definition,
      projections: batch.map((field) => ({ id: field, field })),
      page: { size: definition.page.size, ...(cursors ? { cursor: cursors[index]! } : {}) },
    });
    if (results[0] && identity(results[0]) !== identity(result))
      throw new CopyError(
        viewerStrings.webStateUnifiedRecordPage.associationSourcesOrCountsChangedDuringColumn,
      );
    results.push(result);
  }
  const first = results[0]!;
  const next = results.map((result) => result.nextCursor);
  if (next.some((cursor) => cursor === null) && next.some((cursor) => cursor !== null))
    throw new CopyError(
      viewerStrings.webStateUnifiedRecordPage.inconsistentAssociationColumnCursorsRefreshTheRecords,
    );
  return {
    result: {
      ...first,
      rows: first.rows.map((row, index) => ({
        ...row,
        cells: Object.assign(
          {},
          ...results.map((result) => result.rows[index]!.cells),
        ) as typeof row.cells,
      })),
      output: {
        ...first.output,
        projections: results.flatMap((result) => result.output.projections),
      },
    },
    nextCursors: next.every((cursor): cursor is string => cursor !== null) ? next : null,
  };
}
